export const DEV_ROOT_DOMAIN = 'localhost:4200' as const;

export const EXP_REALTY_CA_ROOT_DOMAIN = 'exprealty.ca' as const;
export const EXP_REALTY_US_ROOT_DOMAIN = 'exprealty.com' as const;
export const EXP_COMMERCIAL_ROOT_DOMAIN = 'expcommercial.com' as const;
export const EXP_COMMERCIAL_CA_ROOT_DOMAIN = 'expcommercial.ca' as const;
export const ZOOCASA_ROOT_DOMAIN = 'zoocasa.com' as const;

export const ZOOCASA_EXP_TEST_ROOT_DOMAIN = 'zooexptest.com' as const;
export const ZOOCASA_EXP_PROD_ROOT_DOMAIN = 'zooexpprod.com' as const;

export const ZOOCASA_EXP_TEST_CA_ROOT_DOMAIN = 'zooexptest.ca' as const;
export const ZOOCASA_EXP_PROD_CA_ROOT_DOMAIN = 'zooexpprod.ca' as const;

export type RootDomain = typeof EXP_REALTY_CA_ROOT_DOMAIN | typeof EXP_REALTY_US_ROOT_DOMAIN | typeof EXP_COMMERCIAL_ROOT_DOMAIN | typeof EXP_COMMERCIAL_CA_ROOT_DOMAIN | typeof ZOOCASA_ROOT_DOMAIN | typeof ZOOCASA_EXP_TEST_ROOT_DOMAIN | typeof ZOOCASA_EXP_PROD_ROOT_DOMAIN | typeof ZOOCASA_EXP_TEST_CA_ROOT_DOMAIN | typeof ZOOCASA_EXP_PROD_CA_ROOT_DOMAIN | typeof DEV_ROOT_DOMAIN;

export const EXP_IN_SERVICE_SUB_DOMAIN = 'inservice' as const;

/**
 * Generates a URL for a root domain with optional HTTPS protocol
 *
 * @param rootDomain The root domain (e.g., 'zoocasa.com')
 * @param useHttps Whether to use HTTPS protocol (defaults to true)
 * @returns The complete URL with protocol and domain
 *
 * @example
 * getRootDomainUrl('zoocasa.com') // returns 'https://zoocasa.com'
 * getRootDomainUrl('zoocasa.com', false) // returns 'http://zoocasa.com'
 */
export function getRootDomainUrl(rootDomain: RootDomain, useHttps: boolean = true) {
  return `${useHttps ? 'https://' : 'http://'}${rootDomain}`;
}

/**
 * Generates a URL for a subdomain with optional HTTPS protocol
 *
 * @param rootDomain The root domain (e.g., 'zoocasa.com')
 * @param subDomain The subdomain (e.g., 'www')
 * @param useHttps Whether to use HTTPS protocol (defaults to true)
 * @returns The complete URL with protocol, subdomain, and domain
 *
 * @example
 * getSubDomainUrl('zoocasa.com', 'www') // returns 'https://www.zoocasa.com'
 * getSubDomainUrl('zoocasa.com', 'blog', false) // returns 'http://blog.zoocasa.com'
 */
export function getSubDomainUrl(rootDomain: RootDomain, subDomain: string, useHttps: boolean = true) {
  return `${useHttps ? 'https://' : 'http://'}${subDomain}.${rootDomain}`;
}

/**
 * Generates a complete URL including subdomain and path
 *
 * @param rootDomain The root domain (e.g., 'zoocasa.com')
 * @param subDomain The subdomain (e.g., 'www')
 * @param path The URL path (e.g., '/about')
 * @param useHttps Whether to use HTTPS protocol (defaults to true)
 * @returns The complete URL with protocol, subdomain, domain, and path
 *
 * @example
 * getSubDomainUrlWithPath('zoocasa.com', 'www', '/about') // returns 'https://www.zoocasa.com/about'
 * getSubDomainUrlWithPath('zoocasa.com', 'www', '/about', false) // returns 'http://www.zoocasa.com/about'
 */
export function getSubDomainUrlWithPath(rootDomain: RootDomain, subDomain: string, path: string, useHttps: boolean = true) {
  return `${getSubDomainUrl(rootDomain, subDomain, useHttps)}${path}`;
}

/**
 * Generates a complete URL including root domain and path
 *
 * @param rootDomain The root domain (e.g., 'zoocasa.com')
 * @param path The URL path (e.g., '/about')
 * @param useHttps Whether to use HTTPS protocol (defaults to true)
 * @returns The complete URL with protocol, root domain, and path
 *
 * @example
 * getRootDomainUrlWithPath('zoocasa.com', '/about') // returns 'https://zoocasa.com/about'
 * getRootDomainUrlWithPath('zoocasa.com', '/about', false) // returns 'http://zoocasa.com/about'
 */
export function getRootDomainUrlWithPath(rootDomain: RootDomain, path: string, useHttps: boolean = true) {
  return `${getRootDomainUrl(rootDomain, useHttps)}${path}`;
}